import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Layout } from "@/components/layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Database,
  RefreshCw,
  Trash2,
  BarChart3,
  Users,
  Target,
  Image,
  TrendingUp,
  AlertCircle,
  CheckCircle2,
  Loader2,
  LayoutGrid,
} from "lucide-react";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";

export default function MockData() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();
  const [selectedBrandId, setSelectedBrandId] = useState<string>("");
  const [seeding, setSeeding] = useState(false);

  const { data: status, isLoading: statusLoading } = useQuery({
    queryKey: ["/api/mock-insights/status"],
    queryFn: () => fetch("/api/mock-insights/status").then((r) => r.json()),
    refetchInterval: seeding ? 3000 : false,
  });

  const { data: brands } = useQuery({
    queryKey: ["/api/brands"],
    queryFn: () => fetch("/api/brands").then((r) => r.json()),
  });

  const { data: seedStatus } = useQuery({
    queryKey: ["/api/mock-insights/seed/status"],
    queryFn: () => fetch("/api/mock-insights/seed/status").then((r) => r.json()),
    refetchInterval: seeding ? 2000 : false,
    enabled: seeding,
  });

  useEffect(() => {
    if (seeding && seedStatus && !seedStatus.inProgress && status?.seeded) {
      setSeeding(false);
      queryClient.invalidateQueries({ queryKey: ["/api/mock-insights"] });
      toast({ title: "Mock data seeded successfully" });
    }
  }, [seeding, seedStatus, status?.seeded]);

  const brandId = selectedBrandId || (status?.brands?.[0]?.brandId ? String(status.brands[0].brandId) : "");

  const { data: accountSummary } = useQuery({
    queryKey: ["/api/mock-insights", brandId, "account-summary"],
    queryFn: () => fetch(`/api/mock-insights/${brandId}/account-summary`).then((r) => r.json()),
    enabled: !!brandId && status?.seeded && !seeding,
  });

  const { data: campaigns } = useQuery({
    queryKey: ["/api/mock-insights", brandId, "campaigns"],
    queryFn: () => fetch(`/api/mock-insights/${brandId}/campaigns`).then((r) => r.json()),
    enabled: !!brandId && status?.seeded && !seeding,
  });

  const { data: adSets } = useQuery({
    queryKey: ["/api/mock-insights", brandId, "ad-sets"],
    queryFn: () => fetch(`/api/mock-insights/${brandId}/ad-sets`).then((r) => r.json()),
    enabled: !!brandId && status?.seeded && !seeding,
  });

  const { data: ads } = useQuery({
    queryKey: ["/api/mock-insights", brandId, "ads"],
    queryFn: () => fetch(`/api/mock-insights/${brandId}/ads`).then((r) => r.json()),
    enabled: !!brandId && status?.seeded && !seeding,
  });

  const { data: dailyTrend } = useQuery({
    queryKey: ["/api/mock-insights", brandId, "daily-trend"],
    queryFn: () => fetch(`/api/mock-insights/${brandId}/daily-trend`).then((r) => r.json()),
    enabled: !!brandId && status?.seeded && !seeding,
  });

  const seedMutation = useMutation({
    mutationFn: () => fetch("/api/mock-insights/seed", { method: "POST" }).then((r) => r.json()),
    onSuccess: () => {
      setSeeding(true);
      toast({ title: "Seeding started", description: "Generating mock data... This may take a moment." });
    },
    onError: () => {
      toast({ title: "Seed failed", variant: "destructive" });
    },
  });

  const clearMutation = useMutation({
    mutationFn: () => fetch("/api/mock-insights/clear", { method: "POST" }).then((r) => r.json()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/mock-insights"] });
      toast({ title: "Mock data cleared" });
    },
  });

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="text-page-title">
              Mock Data Manager
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Manage mock Meta Marketing API data for development and testing
            </p>
          </div>
          <Badge variant="outline" className="text-xs">
            Developer Tools
          </Badge>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <StatusCard
            icon={Database}
            label="Ad Accounts"
            value={status?.counts?.accounts ?? 0}
            loading={statusLoading}
          />
          <StatusCard
            icon={Target}
            label="Campaigns"
            value={status?.counts?.campaigns ?? 0}
            loading={statusLoading}
          />
          <StatusCard
            icon={Users}
            label="Ad Sets"
            value={status?.counts?.adSets ?? 0}
            loading={statusLoading}
          />
          <StatusCard
            icon={Image}
            label="Ads"
            value={status?.counts?.ads ?? 0}
            loading={statusLoading}
          />
          <StatusCard
            icon={BarChart3}
            label="Insight Rows"
            value={status?.counts?.insights ?? 0}
            loading={statusLoading}
          />
        </div>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {status?.seeded ? (
                  <div className="flex items-center gap-2 text-green-600">
                    <CheckCircle2 className="h-5 w-5" />
                    <span className="font-medium">Data seeded</span>
                    <span className="text-sm text-gray-500">
                      — Total mock spend: ${status?.totalSpend?.toLocaleString() ?? "0"}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-amber-600">
                    <AlertCircle className="h-5 w-5" />
                    <span className="font-medium">No mock data</span>
                    <span className="text-sm text-gray-500">
                      — Run the seed script to generate data
                    </span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => seedMutation.mutate()}
                  disabled={seedMutation.isPending || seeding}
                  data-testid="button-reseed"
                >
                  {seedMutation.isPending || seeding ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  {seeding ? "Seeding..." : status?.seeded ? "Reseed Data" : "Seed Data"}
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => clearMutation.mutate()}
                  disabled={clearMutation.isPending || !status?.seeded || seeding}
                  data-testid="button-clear"
                >
                  {clearMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4 mr-2" />
                  )}
                  Clear All Data
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {status?.seeded && (
          <>
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Brand Preview</CardTitle>
                  <div className="flex items-center gap-2">
                    <Select value={brandId} onValueChange={setSelectedBrandId}>
                      <SelectTrigger className="w-[280px]" data-testid="select-brand">
                        <SelectValue placeholder="Select a brand" />
                      </SelectTrigger>
                      <SelectContent>
                        {brands?.map((b: any) => (
                          <SelectItem key={b.id} value={String(b.id)}>
                            {b.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {brandId && (
                      <Button
                        size="sm"
                        className="bg-indigo-500 hover:bg-indigo-600 gap-1.5"
                        onClick={() => navigate(`/meta/dashboard`)}
                        data-testid="button-open-dashboard"
                      >
                        <LayoutGrid className="h-4 w-4" />
                        Dashboard
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              {accountSummary?.hasData && (
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <MetricCard label="Total Spend" value={`$${accountSummary.summary.spend.toLocaleString()}`} />
                    <MetricCard label="Impressions" value={accountSummary.summary.impressions.toLocaleString()} />
                    <MetricCard label="Clicks" value={accountSummary.summary.clicks.toLocaleString()} />
                    <MetricCard label="CTR" value={`${accountSummary.summary.ctr}%`} />
                    <MetricCard label="CPC" value={`$${accountSummary.summary.cpc}`} />
                    <MetricCard label="ROAS" value={`${accountSummary.summary.roas}x`} />
                    <MetricCard label="Purchases" value={String(accountSummary.summary.purchases)} />
                    <MetricCard label="Leads" value={String(accountSummary.summary.leads)} />
                  </div>
                </CardContent>
              )}
            </Card>

            {brandId && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Data Preview</CardTitle>
                </CardHeader>
                <CardContent>
                  <Tabs defaultValue="campaigns">
                    <TabsList>
                      <TabsTrigger value="campaigns" data-testid="tab-campaigns">
                        Campaigns ({campaigns?.length ?? 0})
                      </TabsTrigger>
                      <TabsTrigger value="adsets" data-testid="tab-adsets">
                        Ad Sets ({adSets?.length ?? 0})
                      </TabsTrigger>
                      <TabsTrigger value="ads" data-testid="tab-ads">
                        Ads ({ads?.length ?? 0})
                      </TabsTrigger>
                      <TabsTrigger value="insights" data-testid="tab-insights">
                        Daily Trend ({dailyTrend?.length ?? 0})
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="campaigns" className="mt-4">
                      <DataTable
                        columns={["Name", "Objective", "Status", "Budget", "Spend", "CTR", "ROAS"]}
                        rows={(campaigns || []).slice(0, 50).map((c: any) => [
                          c.name,
                          c.objective,
                          c.status,
                          `$${c.dailyBudget}/day`,
                          `$${c.metrics.spend.toLocaleString()}`,
                          `${c.metrics.ctr}%`,
                          `${c.metrics.roas}x`,
                        ])}
                      />
                    </TabsContent>

                    <TabsContent value="adsets" className="mt-4">
                      <DataTable
                        columns={["Name", "Status", "Age", "Gender", "Spend", "Clicks"]}
                        rows={(adSets || []).slice(0, 50).map((as_: any) => [
                          as_.name.length > 50 ? as_.name.slice(0, 47) + "..." : as_.name,
                          as_.status,
                          as_.targetingAge,
                          as_.targetingGender,
                          `$${as_.metrics.spend.toLocaleString()}`,
                          String(as_.metrics.clicks),
                        ])}
                      />
                    </TabsContent>

                    <TabsContent value="ads" className="mt-4">
                      <DataTable
                        columns={["Name", "Format", "Status", "Spend", "CTR", "ROAS"]}
                        rows={(ads || []).slice(0, 50).map((a: any) => [
                          a.name,
                          a.format,
                          a.status,
                          `$${a.metrics.spend.toLocaleString()}`,
                          `${a.metrics.ctr}%`,
                          `${a.metrics.roas}x`,
                        ])}
                      />
                    </TabsContent>

                    <TabsContent value="insights" className="mt-4">
                      <DataTable
                        columns={["Date", "Spend", "Impressions", "Clicks", "CTR", "ROAS"]}
                        rows={(dailyTrend || []).slice(0, 50).map((d: any) => [
                          d.date,
                          `$${d.spend.toLocaleString()}`,
                          String(d.impressions),
                          String(d.clicks),
                          `${d.ctr}%`,
                          `${d.roas}x`,
                        ])}
                      />
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}

function StatusCard({
  icon: Icon,
  label,
  value,
  loading,
}: {
  icon: any;
  label: string;
  value: number;
  loading: boolean;
}) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-50 rounded-lg">
            <Icon className="h-5 w-5 text-indigo-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">{label}</p>
            <p className="text-xl font-bold text-gray-900" data-testid={`text-count-${label.toLowerCase().replace(/\s+/g, "-")}`}>
              {loading ? "..." : value.toLocaleString()}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function MetricCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="p-3 bg-gray-50 rounded-lg">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-lg font-semibold text-gray-900">{value}</p>
    </div>
  );
}

function DataTable({ columns, rows }: { columns: string[]; rows: string[][] }) {
  if (rows.length === 0) {
    return <p className="text-sm text-gray-500 py-4 text-center">No data available</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b">
            {columns.map((col) => (
              <th key={col} className="text-left py-2 px-3 font-medium text-gray-500">
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b last:border-0 hover:bg-gray-50">
              {row.map((cell, j) => (
                <td key={j} className="py-2 px-3 text-gray-700">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
